import {
  useDebounceState,
  useStableCallback,
} from "@prismicio/editor-support/React"
import { toURL } from "@prismicio/editor-support/URL"
import type { EmbedContent } from "@prismicio/types-internal/lib/content"
import {
  type KeyboardEvent,
  type RefObject,
  startTransition,
  useEffect,
  useState,
} from "react"

import type { EmbedViewProps } from "./EmbedView"

export function useEmbedView(
  props: EmbedViewProps,
  inputRef: RefObject<HTMLInputElement>,
  containerRef: RefObject<HTMLDivElement>,
) {
  const { node } = props
  const content = node.attrs.content
  const updateAttributes = useStableCallback(props.updateAttributes)

  const url = useDebounceState({
    initialState: content?.embed_url ?? "",
    startTransition,
  })

  function onContentChange(content?: EmbedContent) {
    updateAttributes({ content })
  }

  function urlChanged(newUrl: string) {
    url.set(newUrl)
    if (!toURL(newUrl)) onContentChange(undefined)
  }

  const { onKeyDown } = useGestures(props, url.value, inputRef, containerRef)

  return {
    inputRef,
    containerRef,
    onKeyDown,
    url: url.value,
    urlChanged,
    onContentChange,
    debouncedUrl: url.debouncedValue,
    content: props.node.attrs.content,
  }
}

function useGestures(
  props: EmbedViewProps,
  url: string,
  inputRef: RefObject<HTMLInputElement>,
  containerRef: RefObject<HTMLDivElement>,
) {
  const { editor, getPos, node } = props

  const [isFocused, setIsFocused] = useState(true)

  useEffect(() => {
    // required to ensure that focus is given to embed on create
    // but not on screen refresh
    if (!isFocused || !props.editor.isFocused) return

    // use setTimeout to delay the focus to the next tick, otherwise it will fail
    setTimeout(() => inputRef.current?.focus())
  }, [inputRef, isFocused, props.editor.isFocused])

  function onKeyDown(event: KeyboardEvent) {
    const startPos = getPos()
    const endPos = getPos() + node.nodeSize

    if (event.code === "Enter") {
      setIsFocused(false)
      editor
        .chain()
        .insertContentAt(endPos, {
          type: "paragraph",
        })
        // +1 is neccesary to ensure
        // that the focus is set to the end of the next line
        .focus(endPos + 1)
        .run()
    }

    if (event.code === "Backspace") {
      if (url === "") {
        editor
          .chain()
          .deleteRange({ from: startPos, to: endPos })
          .focus(endPos + 1)
          .run()
      }
    }

    if (event.code === "ArrowUp") {
      setIsFocused(false)
      editor
        .chain()
        .focus(startPos - 1)
        .run()
    }

    if (event.code === "ArrowDown") {
      setIsFocused(false)
      editor
        .chain()
        .focus(endPos + 1)
        .run()
    }
  }

  return { containerRef, inputRef, onKeyDown }
}
